'use client'
import { setActiveTenant, setActiveUser, useYourUserData } from '@hermes/api-sdk'
import { useUserToken } from '@hooks/useUserToken'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'

import { PrivateUserDto } from '@hermes/domains'
import { useTokenValidation } from '@hooks/useTokenValidation'
import { useAuthStore } from '@lib/stores/auth'
import React, { useCallback, useEffect } from 'react'

type UserContextProps = {
  user: (PrivateUserDto & { intercomUserHash: string }) | null
  isLoading: boolean
  revalidate: () => Promise<void>
  error: any
}

const UserContext = React.createContext<UserContextProps | null>(null)

const useUser = (): UserContextProps => {
  const context = React.useContext<UserContextProps | null>(UserContext)
  if (context === null) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

const UserProvider: React.FC<{ locale: string; children: React.ReactNode }> = ({ locale, children }) => {
  const { token } = useUserToken()
  const refreshJWT = useAuthStore((state) => state.refreshJWT)
  const region = getRegionFromLocale(locale)
  const tokenValidation = useTokenValidation()

  const { data: user, isLoading, error, refresh } = useYourUserData({ token })

  useEffect(() => {
    if (user && token) {
      setActiveUser({ token: token.toString(), id: user.id })
    }
  }, [user, token])

  useEffect(() => {
    if (region) {
      setActiveTenant(region)
    }
  }, [region])

  useEffect(() => {
    if (refreshJWT && token) {
      tokenValidation.startRefresh()

      // Return cleanup function to stop refresh when effect re-runs or unmounts
      return () => {
        tokenValidation.stopRefresh()
      }
    }
  }, [refreshJWT])

  const handleRevalidate = useCallback(async () => {
    await refresh()
  }, [refresh])

  return (
    <UserContext.Provider value={{ user: user ?? null, revalidate: handleRevalidate, isLoading, error }}>
      {children}
    </UserContext.Provider>
  )
}

export { UserProvider, useUser }
